/**
 * CaseRemindersPanel — dated case reminders awaiting action (Lloyd, Jul 21 2026).
 *
 * Kept separate from the Manager Alerts table below it on purpose. Alerts are dismissible notices;
 * these are outstanding work that must not be dismissible — a CDL Legal claim leaves the list when
 * it has been filed, and only then. Mixing them into a table with a Dismiss button would hand staff
 * a one-click way to lose the firm's money.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Alert,
  Stack,
  CircularProgress,
  Divider,
} from '@mui/material';
import CaseReminderApi, { CaseReminderDto, STAFF_REMINDER } from '../../api/CaseReminderApi';

function daysOverdue(dueAt: string): number {
  const ms = Date.now() - new Date(dueAt).getTime();
  return Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)));
}

const CaseRemindersPanel: React.FC = () => {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState<CaseReminderDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState<string | null>(null);

  const fetchReminders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReminders(await CaseReminderApi.listOpen());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reminders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  const handleResolve = async (reminder: CaseReminderDto) => {
    setResolving(reminder.CaseReminder_guid);
    setError(null);
    try {
      await CaseReminderApi.resolve(reminder.CaseReminder_guid);
      setReminders((current) =>
        current.filter((r) => r.CaseReminder_guid !== reminder.CaseReminder_guid),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update the reminder');
    } finally {
      setResolving(null);
    }
  };

  // Nothing outstanding is the normal state — an empty box every day would train people to skip it.
  if (!loading && !error && reminders.length === 0) return null;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Action needed ({reminders.length})
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        reminders.map((reminder, index) => {
          const overdue = daysOverdue(reminder.CaseReminder_DueAt);
          return (
            <Box key={reminder.CaseReminder_guid}>
              {index > 0 && <Divider sx={{ my: 1.5 }} />}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ alignItems: { sm: 'center' } }}
              >
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {reminder.title}
                    </Typography>
                    {overdue > 0 && (
                      <Chip
                        label={`${overdue} day${overdue === 1 ? '' : 's'} open`}
                        size="small"
                        color={overdue >= 3 ? 'error' : 'warning'}
                        variant="outlined"
                      />
                    )}
                    {reminder.CaseReminder_EscalatedAt && (
                      <Chip label="escalated" size="small" color="error" />
                    )}
                  </Stack>
                  {/* Plain text: a staff note is shown as typed, line breaks kept. */}
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {reminder.body}
                  </Typography>
                  {reminder.CaseReminder_CreatedByName && (
                    <Typography variant="caption" color="text.secondary">
                      Set by {reminder.CaseReminder_CreatedByName}
                    </Typography>
                  )}
                </Box>

                <Stack direction="row" spacing={1}>
                  {reminder.Ticket_guid && (
                    <Button
                      size="small"
                      onClick={() => navigate(`/ticket?Ticket_guid=${reminder.Ticket_guid}`)}
                    >
                      Open case
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    disabled={resolving === reminder.CaseReminder_guid}
                    onClick={() => handleResolve(reminder)}
                  >
                    {resolving === reminder.CaseReminder_guid
                      ? 'Saving…'
                      : reminder.CaseReminder_Type === STAFF_REMINDER
                        ? 'Mark done'
                        : 'Mark filed'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          );
        })
      )}
    </Paper>
  );
};

export default CaseRemindersPanel;
